import { useState, useEffect, useCallback } from "react";
import { FaGithub } from "react-icons/fa6";
import { IoMdMenu } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { HiOutlineArrowDownTray } from "react-icons/hi2";
import { NAV_LINKS, SITE, SOCIALS } from "../data/portfolio";

const GITHUB = SOCIALS.find((s) => s.icon === "github")?.href || "#";

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!sections.length || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = useCallback((e, id) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-line bg-bg/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="container-page flex h-[68px] items-center justify-between">
        <a
          href="#home"
          onClick={(e) => go(e, "home")}
          className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-ink no-underline"
        >
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-accent to-accent-deep text-sm text-white shadow-glow">
            {SITE.shortName}
          </span>
          <span className="hidden sm:inline">
            abdullah<span className="text-accent-soft">.dev</span>
          </span>
        </a>

        <ul className="m-0 hidden list-none items-center gap-1 p-0 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.id}>
              <a
                href={`#${link.id}`}
                onClick={(e) => go(e, link.id)}
                className={`relative rounded-lg px-3 py-2 text-sm font-medium no-underline transition-colors ${
                  active === link.id ? "text-ink" : "text-ink-muted hover:text-ink"
                }`}
              >
                {link.label}
                {active === link.id && (
                  <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-accent" />
                )}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <a
            href={GITHUB}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub"
            className="hidden h-9 w-9 place-items-center rounded-lg border border-line bg-white/5 text-ink transition-colors hover:border-accent/50 hover:text-accent-soft sm:grid"
          >
            <FaGithub />
          </a>
          <a
            href="#resume"
            onClick={(e) => go(e, "resume")}
            className="btn btn-primary hidden !px-4 !py-2 text-sm sm:inline-flex"
          >
            <HiOutlineArrowDownTray />
            Resume
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-white/5 text-ink md:hidden"
          >
            {open ? <RxCross2 size={22} /> : <IoMdMenu size={22} />}
          </button>
        </div>
      </nav>

      <div
        className={`overflow-hidden border-t border-line bg-bg/95 backdrop-blur-xl transition-[max-height] duration-300 md:hidden ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <ul className="m-0 flex list-none flex-col gap-1 p-4">
          {NAV_LINKS.map((link) => (
            <li key={link.id}>
              <a
                href={`#${link.id}`}
                onClick={(e) => go(e, link.id)}
                className={`block rounded-lg px-4 py-3 text-base font-medium no-underline ${
                  active === link.id
                    ? "bg-accent/10 text-ink"
                    : "text-ink-muted hover:bg-white/5 hover:text-ink"
                }`}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
};

export default NavBar;
